import { useCallback, useRef, useState } from 'react';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { sentryCaptureException } from '@/utils/sentry';

/**
 * Copy text to the clipboard using the Clipboard API with fallback.
 * Returns true on success, false on failure.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    try {
      textArea.focus();
      textArea.select();
      return document.execCommand('copy');
    } catch (error) {
      sentryCaptureException(error);
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
}

/**
 * Hook for copy to clipboard functionality with feedback state.
 * Returns { copied, handleCopy } — copied resets after 2 seconds.
 */
export function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useMountEffect(() => {
    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    };
  });

  const handleCopy = useCallback(async (text: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(true);
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }
    return success;
  }, []);

  return { copied, handleCopy };
}
